import React, { useState, useRef, useEffect } from "react";
import { Tabs, Input, Select, Switch, Button, message, Card } from "antd";
import { BsTrash, BsPlus } from "react-icons/bs";
import { FiImage } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { createCategory, createSubcategory } from "../../../api/AdminRequest";

const { TabPane } = Tabs;

function AddSubCategory({ categoryId, categoryName, onSuccess, onCancel, hideHeader = false }) {
    // General tab state
    const [name, setName] = useState("");
    const [value, setValue] = useState("");
    const [status, setStatus] = useState("active");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // Forms tab state
    const [formFields, setFormFields] = useState([]);
    const [submitLoading, setSubmitLoading] = useState(false);

    // Gallery Initial state
    const [gallerySections, setGallerySections] = useState([]);

    // Field type options
    const fieldTypes = [
        { value: "text", label: "Text Input" },
        { value: "textarea", label: "Text Area" },
        { value: "select", label: "Dropdown Select" },
        { value: "date", label: "Date Picker" },
    ];

    const pickingTypes = [
        { value: "date", label: "Full Date" },
        { value: "year", label: "Year Only" },
        { value: "month", label: "Month & Year" },
    ];

    useEffect(() => {
        console.log("categoryId in subcategory", categoryId);
    }, [categoryId]);


    // Image handlers
    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                message.error("Please upload an image file");
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                message.error("Image size should be less than 5MB");
                return;
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Form field handlers
    const addFormField = () => {
        const newField = {
            id: Date.now(),
            label: "",
            name: "",
            type: "text",
            required: false,
            show: true,
            icon: null,  // { pack: "fi", name: "FiUser" } format
            values: [],
            pickingType: "date",
        };
        setFormFields([...formFields, newField]);
    };

    const updateFormField = (index, key, value) => {
        const updatedFields = [...formFields];
        updatedFields[index][key] = value;

        // Auto-generate name from label
        if (key === "label") {
            updatedFields[index].name = value
                .toLowerCase()
                .replace(/\s+/g, "")
                .replace(/[^a-zA-Z0-9]/g, "");
        }

        setFormFields(updatedFields);
    };

    const removeFormField = (index) => {
        const updatedFields = formFields.filter((_, i) => i !== index);
        setFormFields(updatedFields);
    };

    // Select values handlers
    const addSelectValue = (fieldIndex) => {
        const updatedFields = [...formFields];
        updatedFields[fieldIndex].values.push({ label: "", value: "" });
        setFormFields(updatedFields);
    };

    const updateSelectValue = (fieldIndex, valueIndex, key, val) => {
        const updatedFields = [...formFields];
        updatedFields[fieldIndex].values[valueIndex][key] = val;

        // Auto-generate value from label
        if (key === "label") {
            updatedFields[fieldIndex].values[valueIndex].value = val
                .toLowerCase()
                .replace(/\s+/g, "_");
        }

        setFormFields(updatedFields);
    };

    const removeSelectValue = (fieldIndex, valueIndex) => {
        const updatedFields = [...formFields];
        updatedFields[fieldIndex].values = updatedFields[fieldIndex].values.filter(
            (_, i) => i !== valueIndex
        );
        setFormFields(updatedFields);
    };

    // Gallery handlers
    const addGallerySection = () => {
        setGallerySections([...gallerySections, { id: Date.now(), name: "" }]);
    };

    const updateGallerySection = (index, value) => {
        const updated = [...gallerySections];
        updated[index].name = value;
        setGallerySections(updated);
    };

    const removeGallerySection = (index) => {
        setGallerySections(gallerySections.filter((_, i) => i !== index));
    };

    // Convert gallerySections to the required format
    const getGalleryInitialData = () => {
        const galleryInitial = {};
        gallerySections.forEach((section) => {
            if (section.name.trim()) {
                galleryInitial[section.name.trim()] = [""];
            }
        });
        return galleryInitial;
    };

    // Submit handler - combines category and subcategory into unified API call
    const handleSubmit = async () => {
        if (!name.trim()) {
            message.error("Please enter subcategory name");
            return;
        }
        if (!value.trim()) {
            message.error("Please enter subcategory value");
            return;
        }
        if (!imageFile) {
            message.error("Please select a subcategory image");
            return;
        }
        if (formFields.length === 0) {
            message.error("Please add at least one form field");
            return;
        }
        if (gallerySections.length === 0) {
            message.error("Please add at least one gallery section");
            return;
        }

        try {

            setSubmitLoading(true);

            // Create FormData for multipart request (needed for image upload)
            const formData = new FormData();

            // Build subcategory data
            const subcategoryData = {
                name: name.trim(),
                value: value.trim(),
                status: status,
                form: formFields.map(({ id, ...rest }) => rest),
                galleryInitial: getGalleryInitialData()
            };

            // Check if categoryId is an object (new category flow) or string (existing category)
            const isNewCategoryFlow = typeof categoryId === 'object' && categoryId !== null && !(categoryId instanceof FormData);

            if (isNewCategoryFlow) {
                // New category flow - add category data
                formData.append("name", categoryId.name);
                formData.append("value", categoryId.value);
                formData.append("status", categoryId.status);
                if (categoryId.imageFile) {
                    formData.append("image", categoryId.imageFile);
                }
                if (categoryId.icon) {
                    formData.append("icon", JSON.stringify(categoryId.icon));
                }
                formData.append("subcategory", JSON.stringify([subcategoryData]));

                // Log for debugging
                console.log("New Category FormData:");
                for (let [key, val] of formData.entries()) {
                    console.log(`${key}:`, val);
                }

                // Call createCategory API for new category flow
                const { data } = await createCategory(formData);
                if (data?.success) {
                    message.success("Category and subcategory created successfully!");
                    onSuccess?.();
                }
            } else {
                // Existing category flow - use createSubcategory API
                formData.append("categoryID", categoryId);
                formData.append("subcategory", JSON.stringify([subcategoryData]));

                // Append subcategory image if available
                if (imageFile) {
                    formData.append("image", imageFile);
                }

                // Log for debugging
                console.log("Existing Category - Create Subcategory FormData:");
                for (let [key, val] of formData.entries()) {
                    console.log(`${key}:`, val);
                }

                // Call createSubcategory API for existing category flow
                const { data } = await createSubcategory(formData);
                if (data?.success) {
                    message.success("Subcategory created successfully!");
                    onSuccess?.();
                }
            }
        } catch (error) {
            console.log(error);
            message.error(error?.response?.data?.message || "Failed to create category");
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className="flex-grow overflow-y-auto scrollbar-hide p-4 lg:p-6 md:p-5">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 md:p-6">
                {/* Header - conditionally rendered */}
                {!hideHeader && (
                    <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
                        <div className="text-left w-full">
                            <h2 className="text-2xl font-bold text-gray-800">Add Sub Category</h2>
                            <p className="text-gray-500 text-sm mt-1">
                                {categoryName ? (
                                    <>Adding subcategory to <span className="font-semibold text-[#1955EB]">{categoryName}</span></>
                                ) : (
                                    "Create a new subcategory with custom form fields"
                                )}
                            </p>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="bg-white rounded-lg">
                    <Tabs defaultActiveKey="general" className="category-tabs">
                        {/* General Tab */}
                        <TabPane tab="General" key="general">
                            <div className="space-y-6 max-w-2xl">
                                {/* Subcategory Image - same UI as CreateBidForm Photos */}
                                <div className="mb-6 text-left">
                                    <h3 className="text-lg font-semibold mb-1 text-gray-900 text-left">
                                        Subcategory Image <span className="text-red-500">*</span>
                                    </h3>
                                    <p className="text-sm text-gray-500 mb-4 text-left">
                                        Add one photo for this subcategory.
                                    </p>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageChange}
                                        accept="image/*"
                                        className="hidden"
                                    />
                                    <div className="flex gap-3 overflow-x-auto pb-2">
                                        {imagePreview ? (
                                            <div className="relative w-28 h-28 rounded-xl overflow-hidden border shadow-sm shrink-0 transition-transform duration-300">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="w-full h-full object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={removeImage}
                                                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-black"
                                                    aria-label="Remove image"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={handleImageClick}
                                            className="w-28 h-28 shrink-0 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center hover:border-blue-500 transition cursor-pointer bg-white"
                                        >
                                            <span className="text-2xl leading-none mb-1">+</span>
                                            <span className="text-sm text-gray-600">Upload</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="my-6 border-t border-gray-200" />

                                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-left">Subcategory Info</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Subcategory Name <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            placeholder="Enter subcategory name (e.g., Cars)"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="h-10"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Subcategory Value <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            placeholder="Enter subcategory value (e.g., cars)"
                                            value={value}
                                            onChange={(e) => setValue(e.target.value)}
                                            className="h-10"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Status
                                        </label>
                                        <Select
                                            value={status}
                                            onChange={(val) => setStatus(val)}
                                            className="w-full"
                                            style={{ height: 40 }}
                                            options={[
                                                { value: "active", label: "Active" },
                                                { value: "inactive", label: "Inactive" },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        </TabPane>

                        {/* Forms Tab */}
                        <TabPane tab="Form Builder" key="forms">
                            <div className="space-y-4">
                                {/* Add Field Button */}
                                <div className="flex justify-between items-center mb-4">
                                    <p className="text-gray-600 text-sm">
                                        Define custom fields for this subcategory
                                    </p>
                                    <Button
                                        type="primary"
                                        icon={<BsPlus className="text-lg" />}
                                        onClick={addFormField}
                                        className="bg-[#1955EB] hover:bg-[#1945E0]"
                                    >
                                        Add Field
                                    </Button>
                                </div>

                                {/* Form Fields */}
                                {formFields.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                        <p className="text-gray-500">No form fields added yet</p>
                                        <p className="text-gray-400 text-sm mt-1">
                                            Click "Add Field" to create custom form fields
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {formFields.map((field, index) => (
                                            <Card
                                                key={field.id}
                                                className="border border-gray-200 shadow-sm"
                                                size="small"
                                            >
                                                <div className="space-y-4">
                                                    {/* Field Header */}
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <MdDragIndicator className="text-gray-400 cursor-move" />
                                                            <span className="font-medium text-gray-700">
                                                                Field {index + 1}
                                                            </span>
                                                        </div>
                                                        <Button
                                                            danger
                                                            size="small"
                                                            icon={<BsTrash />}
                                                            onClick={() => removeFormField(index)}
                                                        />
                                                    </div>

                                                    {/* Field Properties */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {/* Label */}
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                                                Label
                                                            </label>
                                                            <Input
                                                                placeholder="Field label"
                                                                value={field.label}
                                                                onChange={(e) =>
                                                                    updateFormField(index, "label", e.target.value)
                                                                }
                                                                size="small"
                                                            />
                                                        </div>

                                                        {/* Name (auto-generated) */}
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                                                Name (auto)
                                                            </label>
                                                            <Input
                                                                placeholder="field_name"
                                                                value={field.name}
                                                                onChange={(e) =>
                                                                    updateFormField(index, "name", e.target.value)
                                                                }
                                                                size="small"
                                                            />
                                                        </div>

                                                        {/* Type */}
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                                                Type
                                                            </label>
                                                            <Select
                                                                value={field.type}
                                                                onChange={(val) => updateFormField(index, "type", val)}
                                                                className="w-full"
                                                                size="small"
                                                                options={fieldTypes}
                                                            />
                                                        </div>

                                                        {/* Date Picking Type */}
                                                        {field.type === "date" && (
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                                                    Picking Type
                                                                </label>
                                                                <Select
                                                                    value={field.pickingType}
                                                                    onChange={(val) =>
                                                                        updateFormField(index, "pickingType", val)
                                                                    }
                                                                    className="w-full"
                                                                    size="small"
                                                                    options={pickingTypes}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Switches */}
                                                    <div className="flex flex-wrap gap-6 pt-2">
                                                        <div className="flex items-center gap-2">
                                                            <Switch
                                                                checked={field.required}
                                                                onChange={(val) =>
                                                                    updateFormField(index, "required", val)
                                                                }
                                                                size="small"
                                                            />
                                                            <span className="text-sm text-gray-600">Required</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Switch
                                                                checked={field.show}
                                                                onChange={(val) => updateFormField(index, "show", val)}
                                                                size="small"
                                                            />
                                                            <span className="text-sm text-gray-600">Show</span>
                                                        </div>
                                                    </div>

                                                    {/* Select Values */}
                                                    {field.type === "select" && (
                                                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                                            <div className="flex justify-between items-center mb-3">
                                                                <label className="text-xs font-medium text-gray-500">
                                                                    Dropdown Options
                                                                </label>
                                                                <Button
                                                                    type="dashed"
                                                                    size="small"
                                                                    icon={<BsPlus />}
                                                                    onClick={() => addSelectValue(index)}
                                                                >
                                                                    Add Option
                                                                </Button>
                                                            </div>
                                                            <div className="space-y-2">
                                                                {field.values.map((val, valIndex) => (
                                                                    <div
                                                                        key={valIndex}
                                                                        className="flex items-center gap-2"
                                                                    >
                                                                        <Input
                                                                            placeholder="Label"
                                                                            value={val.label}
                                                                            onChange={(e) =>
                                                                                updateSelectValue(
                                                                                    index,
                                                                                    valIndex,
                                                                                    "label",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            size="small"
                                                                            className="flex-1"
                                                                        />
                                                                        <Input
                                                                            placeholder="Value"
                                                                            value={val.value}
                                                                            onChange={(e) =>
                                                                                updateSelectValue(
                                                                                    index,
                                                                                    valIndex,
                                                                                    "value",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            size="small"
                                                                            className="flex-1"
                                                                        />
                                                                        <Button
                                                                            danger
                                                                            size="small"
                                                                            icon={<BsTrash className="text-xs" />}
                                                                            onClick={() => removeSelectValue(index, valIndex)}
                                                                        />
                                                                    </div>
                                                                ))}
                                                                {field.values.length === 0 && (
                                                                    <p className="text-gray-400 text-xs text-center py-2">
                                                                        No options added
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabPane>

                        {/* Gallery Initial Tab */}
                        <TabPane tab="Gallery Sections" key="gallery">
                            <div className="space-y-4">
                                {/* Add Section Button */}
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <p className="text-gray-600 text-sm">
                                            Define image gallery sections for this subcategory
                                        </p>
                                        <p className="text-gray-400 text-xs mt-1">
                                            These sections will be used for image uploads when creating bids
                                        </p>
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<BsPlus className="text-lg" />}
                                        onClick={addGallerySection}
                                        className="bg-[#1955EB] hover:bg-[#1945E0]"
                                    >
                                        Add Section
                                    </Button>
                                </div>

                                {/* Gallery Sections */}
                                {gallerySections.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                        <FiImage className="text-4xl text-gray-400 mx-auto mb-3" />
                                        <p className="text-gray-500">No gallery sections added yet</p>
                                        <p className="text-gray-400 text-sm mt-1">
                                            Click "Add Section" to create gallery sections like "Front View", "Side View", etc.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {gallerySections.map((section, index) => (
                                            <div
                                                key={section.id}
                                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                                            >
                                                <MdDragIndicator className="text-gray-400 cursor-move" />
                                                <Input
                                                    placeholder="Section name (e.g., Front View, Side View)"
                                                    value={section.name}
                                                    onChange={(e) => updateGallerySection(index, e.target.value)}
                                                    className="flex-1"
                                                />
                                                <Button
                                                    danger
                                                    icon={<BsTrash />}
                                                    onClick={() => removeGallerySection(index)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Preview */}
                                {gallerySections.some(s => s.name.trim()) && (
                                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                        <p className="text-sm font-medium text-blue-800 mb-2">Data Preview:</p>
                                        <pre className="text-xs text-blue-700 overflow-auto">
                                            {JSON.stringify(getGalleryInitialData(), null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </TabPane>
                    </Tabs>

                    {/* Footer Buttons - same as CreateBidForm */}
                    <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4">
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={submitLoading}
                        >
                            Create Subcategory
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddSubCategory;
